import { fmtAgo, fmtUtc } from "./format";
import type { DotStatus } from "../components/SignalDot";
import type { Box } from "../types/boxes";

/** Resolved Pep/Plug/UPS/TS indicator props for a single box. */
export interface BoxSignalDots {
  pep: {
    status: DotStatus | null;
    inline: string;
    title: string;
  };
  plug: {
    status: DotStatus | null;
    inline: string;
    title: string;
  };
  ups: {
    status: DotStatus | null;
    inline: string;
    title: string;
  };
  ts: {
    status: DotStatus | null;
    inline: string;
    title: string;
  };
  sl: {
    status: DotStatus | null;
    inline: string;
    title: string;
  };
}

/**
 * Derive Pep + Plug + UPS + Tailscale indicator-dot props from a Box.
 * Single source of truth for the mapping so BoxesPage / OutagesPage /
 * MapPage / SignalDetailPage all render the same dot for the same box.
 */
export function deriveSignalDots(b: Box): BoxSignalDots {
  return {
    pep: derivePep(b),
    plug: derivePlug(b),
    ups: deriveUps(b),
    ts: deriveTs(b),
    sl: deriveStarlink(b),
  };
}

function deriveStarlink(b: Box) {
  // == null catches null + undefined. When no dish data, the FE
  // suppresses the dot entirely (status=null → SignalDot renders
  // nothing).
  const sl = b.starlink;
  if (sl == null) {
    return { status: null, inline: "", title: "" };
  }
  if (!sl.reachable) {
    return {
      status: "unknown" as const,
      inline: "no data",
      title: `Starlink: not reachable${sl.error_message ? ` (${sl.error_message})` : ""}`,
    };
  }
  // Connected + not obstructed + no concerning alerts → green.
  // Obstructed or "OBSTRUCTED" state → amber.
  // Anything else (BOOTING, SEARCHING, OFFLINE-ish) → red.
  const isConnected = (sl.state ?? "").toUpperCase() === "CONNECTED";
  const obstructed = !!sl.currently_obstructed;
  const status: DotStatus =
    isConnected && !obstructed ? "ok" : obstructed ? "warn" : "bad";
  const inline = isConnected
    ? `${(sl.pop_ping_latency_ms ?? 0).toFixed(0)}ms`
    : (sl.state ?? "unknown").toLowerCase();
  const obs = sl.fraction_obstructed;
  const title = [
    `Starlink: ${sl.state ?? "?"}`,
    sl.pop_ping_latency_ms != null
      ? `latency ${sl.pop_ping_latency_ms.toFixed(0)}ms`
      : null,
    obs != null && obs > 0 ? `obstructed ${(obs * 100).toFixed(2)}%` : null,
    sl.gps_sats != null ? `GPS sats ${sl.gps_sats}` : null,
    sl.active_alerts ? `alerts: ${sl.active_alerts}` : null,
    sl.polled_utc ? `polled ${fmtAgo(sl.polled_utc)} ago` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, inline, title };
}

function derivePep(b: Box) {
  // == null catches both null AND undefined — customer-scoped boxes omit
  // operator fields entirely, so these arrive undefined, not null.
  if (b.peplink_status == null) {
    return { status: null, inline: "", title: "" };
  }
  // Status → dot colour
  const status: DotStatus =
    b.peplink_status === "online"
      ? "ok"
      : b.peplink_status === "offline"
        ? "bad"
        : "unknown";

  // Inline duration: which timestamp matches the current state?
  // Falls back to peplink_status_polled_utc when the more specific one is
  // null (upstream sometimes leaves offline_at_utc unset).
  const sinceTs =
    b.peplink_status === "online"
      ? b.peplink_last_online_utc ?? b.peplink_status_polled_utc
      : b.peplink_status === "offline"
        ? b.peplink_offline_at_utc ?? b.peplink_status_polled_utc
        : b.peplink_status_polled_utc;
  const inline =
    b.peplink_status === "online"
      ? `online ${fmtAgo(sinceTs)}`.trim()
      : b.peplink_status === "offline"
        ? `offline ${fmtAgo(sinceTs)}`.trim()
        : "unknown";

  // Tooltip: longer prose with the actual timestamps + address if present
  const title = [
    `Peplink: ${b.peplink_status}`,
    b.peplink_last_online_utc
      ? `last online ${fmtUtc(b.peplink_last_online_utc)} UTC`
      : null,
    b.peplink_offline_at_utc
      ? `offline at ${fmtUtc(b.peplink_offline_at_utc)} UTC`
      : null,
    b.peplink_status_polled_utc
      ? `polled ${fmtUtc(b.peplink_status_polled_utc)} UTC`
      : null,
    b.peplink_address ? `at ${b.peplink_address}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return { status, inline, title };
}

function deriveUps(b: Box) {
  // SCU isn't running gen5_edge_battery (most boxes — Cyberpower UPS with no
  // printer cable means apcupsd doesn't run). Render nothing. == null also
  // catches undefined (customer-scoped boxes omit ups_* fields entirely).
  if (b.ups_state == null) {
    return { status: null, inline: "", title: "" };
  }
  // LWT says the edge service is offline → we don't trust the cached
  // edge_state regardless of what it last said. Downgrade to gray.
  const lwtOffline = b.ups_transport_status === "offline";
  const status: DotStatus = lwtOffline
    ? "unknown"
    : b.ups_state === "SHORE_POWER"
      ? "ok"
      : b.ups_state === "ON_BATTERY"
        ? "warn"
        : b.ups_state === "LOW_BATTERY" ||
            b.ups_state === "SHUTDOWN_PENDING"
          ? "bad"
          : "unknown"; // COMMS_LOST / UNKNOWN
  // Inline label: short state + duration when we have it
  const sinceTs =
    b.ups_state === "SHORE_POWER"
      ? b.ups_shore_power_since_utc
      : b.ups_state === "ON_BATTERY" ||
          b.ups_state === "LOW_BATTERY" ||
          b.ups_state === "SHUTDOWN_PENDING"
        ? b.ups_on_battery_since_utc
        : null;
  const shortLabel = lwtOffline
    ? "edge-offline"
    : b.ups_state === "SHORE_POWER"
      ? "shore"
      : b.ups_state === "ON_BATTERY"
        ? "on-batt"
        : b.ups_state === "LOW_BATTERY"
          ? "low-batt"
          : b.ups_state === "SHUTDOWN_PENDING"
            ? "shutdown"
            : b.ups_state === "COMMS_LOST"
              ? "comms-lost"
              : "unknown";
  const inline = sinceTs
    ? `${shortLabel} ${fmtAgo(sinceTs)}`.trim()
    : shortLabel;
  // Tooltip: more detail — battery%, runtime, line voltage when known
  const title = [
    `UPS: ${b.ups_state}`,
    lwtOffline ? "edge service offline (LWT)" : null,
    b.ups_battery_pct != null ? `${b.ups_battery_pct.toFixed(0)}% batt` : null,
    b.ups_runtime_min != null
      ? `${b.ups_runtime_min.toFixed(0)} min runtime`
      : null,
    b.ups_line_voltage_v != null
      ? `${b.ups_line_voltage_v.toFixed(0)}V line`
      : null,
    b.ups_replace_recommended ? "battery replacement recommended" : null,
    b.ups_last_seen_utc
      ? `last reading ${fmtUtc(b.ups_last_seen_utc)} UTC`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, inline, title };
}

function deriveTs(b: Box) {
  if (b.tailscale_status == null) {
    return { status: null, inline: "", title: "" };
  }
  const status: DotStatus =
    b.tailscale_status === "online"
      ? "ok"
      : b.tailscale_status === "offline"
        ? "bad"
        : "unknown";
  const inline =
    b.tailscale_last_seen_utc !== null
      ? `${b.tailscale_status} ${fmtAgo(b.tailscale_last_seen_utc)}`.trim()
      : b.tailscale_status;
  const title = [
    `Tailscale: ${b.tailscale_status}`,
    b.tailscale_last_seen_utc
      ? `last seen ${fmtUtc(b.tailscale_last_seen_utc)} UTC`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, inline, title };
}

/**
 * Plug indicator dispatch. Two render shapes:
 *   - per-outlet (migration 054, 2026-06-01): when the plug is online
 *     AND we have non-null bits for both outlets, surface each outlet
 *     separately so partial-on situations don't hide behind the green
 *     aggregate dot.
 *   - aggregate (legacy): single on/off/offline state. Used when the
 *     plug is offline OR for any snapshot that pre-dates 054.
 * Both helpers return the same `{ status, inline, title }` shape so
 * the SignalDot call sites don't care which path produced it.
 */
function derivePlug(b: Box) {
  if (b.plug_status == null) {
    return { status: null, inline: "", title: "" };
  }
  const hasPerOutlet =
    b.plug_is_online === true &&
    (b.plug_outlet_count ?? 0) >= 2 &&
    b.plug_outlet1_on != null &&
    b.plug_outlet2_on != null;
  return hasPerOutlet ? derivePlugPerOutlet(b) : derivePlugAggregate(b);
}

function derivePlugAggregate(b: Box) {
  // Same shape as before migration 054. Used for offline plugs (the
  // per-outlet bits are NULL when the plug can't reach Hubspace) and
  // for any snapshot that pre-dates the per-outlet projection.
  const status: DotStatus = b.plug_status === "on" ? "ok" : "bad";
  const sinceTs =
    b.plug_status === "on" ? b.plug_on_since_utc : b.plug_off_since_utc;
  const inlineLabel =
    b.plug_status === "on"
      ? "on"
      : b.plug_status === "offline"
        ? "offline"
        : "off";
  const inline = `${inlineLabel} ${fmtAgo(sinceTs)}`.trim();
  const detail =
    b.plug_status === "offline"
      ? "device unreachable (lost wifi — often paired with lost wall power)"
      : b.plug_status === "off"
        ? "switched off (device reachable)"
        : "delivering power";
  const title = [
    `Smartplug: ${b.plug_status} — ${detail}`,
    sinceTs ? `since ${fmtUtc(sinceTs)} UTC` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, inline, title };
}

function derivePlugPerOutlet(b: Box) {
  // Guard contract: derivePlug only routes here when both outlet bits
  // are non-null. The casts capture that invariant for the rest of the
  // function so each branch reads cleanly.
  const o1 = b.plug_outlet1_on as boolean;
  const o2 = b.plug_outlet2_on as boolean;
  const bothOn = o1 && o2;
  const bothOff = !o1 && !o2;
  const status: DotStatus = bothOn ? "ok" : bothOff ? "bad" : "warn";

  // Inline duration uses the more-recently-changed outlet's since
  // timestamp (i.e. the larger of the two) — that's the one that's
  // most likely interesting ("outlet 2 just flipped off 12m ago").
  const s1 = b.plug_outlet1_since_utc;
  const s2 = b.plug_outlet2_since_utc;
  const recentSince: string | null =
    s1 && s2 ? (s1 > s2 ? s1 : s2) : (s1 ?? s2 ?? null);
  const ago = fmtAgo(recentSince);
  const inlinePorts = `1:${o1 ? "on" : "off"} 2:${o2 ? "on" : "off"}`;
  const inline = `${inlinePorts}${ago ? " " + ago : ""}`.trim();

  const sinceLine = (label: string, on: boolean, ts: string | null): string =>
    `${label}: ${on ? "on" : "off"}${ts ? ` (since ${fmtUtc(ts)} UTC)` : ""}`;
  const summary = bothOn
    ? "both outlets delivering power"
    : bothOff
      ? "both outlets off"
      : "only one outlet delivering power — check what's plugged into the off port";
  const title = [
    `Smartplug: ${summary}`,
    sinceLine("Outlet 1", o1, s1),
    sinceLine("Outlet 2", o2, s2),
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, inline, title };
}
